import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getStorageUrl } from "@/lib/storage";

export interface LibraryImage {
  name: string;
  path: string;
  url: string;
  created_at: string;
}

export function useImageLibrary() {
  return useQuery({
    queryKey: ["image-library"],
    queryFn: async (): Promise<LibraryImage[]> => {
      const { data: files, error } = await supabase.storage
        .from("course-content")
        .list("thumbnails", {
          limit: 100,
          sortBy: { column: "created_at", order: "desc" },
        });

      if (error) throw error;

      const images = (files || [])
        .filter((file) => file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i))
        .map((file) => {
          const path = `thumbnails/${file.name}`;
          return {
            name: file.name,
            path,
            url: getStorageUrl("course-content", path) || "",
            created_at: file.created_at || "",
          };
        });

      return images;
    },
  });
}

/**
 * Uploads a thumbnail image and returns the storage PATH (not full URL)
 */
export async function uploadThumbnail(file: File): Promise<string> {
  const fileExt = file.name.split(".").pop();
  const fileName = `${crypto.randomUUID()}.${fileExt}`;
  const filePath = `thumbnails/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from("course-content")
    .upload(filePath, file);

  if (uploadError) throw uploadError;

  // Return the path, not the full URL
  return filePath;
}

/**
 * Deletes a thumbnail image from storage
 */
export async function deleteThumbnail(path: string): Promise<void> {
  const { error } = await supabase.storage
    .from("course-content")
    .remove([path]);

  if (error) throw error;
}
